import React from 'react';
import {
  Drawer,
  Stack,
  Box,
  CircularProgress,
  Typography,
  Skeleton,
  Divider,
  useTheme,
} from '@mui/material';
import FlyoutHeader from './FlyoutHeader';
import {
  FLYOUT_WIDTH,
  FLYOUT_CONTENT_PADDING_X,
  FLYOUT_CONTENT_PADDING_Y,
} from '../constants';

interface FlyoutLoadingStateProps {
  open: boolean;
  onClose: () => void;
  message?: string;
  showProgress?: boolean;
  dataFieldsCount?: number;
  tabsCount?: number;
  drawerClassName?: string;
  contentClassName?: string;
}

const FlyoutLoadingState: React.FC<FlyoutLoadingStateProps> = ({
  open,
  onClose,
  message = 'Loading details...',
  showProgress = true,
  dataFieldsCount = 6,
  tabsCount = 3,
  drawerClassName = 'holding-details-flyout',
  contentClassName = 'holding-details-flyout-content',
}) => {
  const theme = useTheme();

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      variant='temporary'
      className={drawerClassName}
    >
      <Stack
        sx={{ width: FLYOUT_WIDTH }}
        className={contentClassName}
      >
        {/* Header Skeleton */}
        <FlyoutHeader
          title={
            <Skeleton
              variant='text'
              width='60%'
              height={28}
            />
          }
          subtitle={
            <Skeleton
              variant='text'
              width='40%'
              height={20}
            />
          }
          onClose={onClose}
        />

        {/* Content Loading */}
        <Stack
          direction='column'
          px={FLYOUT_CONTENT_PADDING_X}
          py={FLYOUT_CONTENT_PADDING_Y}
          spacing={4}
        >
          {/* Loading indicator (optional) */}
          {showProgress && (
            <Box
              display='flex'
              flexDirection='column'
              alignItems='center'
              justifyContent='center'
              py={4}
            >
              <CircularProgress
                size={40}
                sx={{ mb: 2 }}
              />
              <Typography
                variant='body2'
                color='text.secondary'
              >
                {message}
              </Typography>
            </Box>
          )}

          {/* Data fields skeleton */}
          <Box
            display='flex'
            flexWrap='wrap'
            mx={-1}
          >
            {Array.from({ length: dataFieldsCount }).map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: '33.33%',
                  p: 1,
                  [theme.breakpoints.down(801)]: {
                    width: '50%',
                  },
                }}
              >
                <Skeleton
                  variant='text'
                  width='70%'
                  height={24}
                  sx={{ mb: 1 }}
                />
                <Skeleton
                  variant='text'
                  width='50%'
                  height={20}
                />
              </Box>
            ))}
          </Box>

          {/* Tabs skeleton */}
          <Stack
            direction='column'
            spacing={2}
          >
            <Divider />

            <Stack
              direction='row'
              spacing={2}
            >
              {Array.from({ length: tabsCount }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant='text'
                  width={80}
                  height={40}
                />
              ))}
            </Stack>

            <Skeleton
              variant='rectangular'
              width='100%'
              height={200}
            />
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default FlyoutLoadingState;
